// The round effect recipes, mockup round 042 (R924).
//
// One entry per catalog code the owner picked for a round, keyed by the
// code of `data/contracts/effects.toml`. `effect-styles.ts` prefers this map
// over the row's family, so a code that is not here yet draws its family
// and the map fills in one card at a time. All twenty cards are here: the
// nineteen of R924 and `halo_round`, which the owner added later (R960).
//
// **A recipe reads its figures out of the row and holds no literal.** The
// row carries `periodSec`, `radiusPx`, `alpha` and `count`, plus the six
// figure fields `radius2Px`, `widthPx`, `lengthPx`, `alpha2`, `speed` and
// `count2`; `data/presentation/effects.toml` names what each one means for
// each card. A round runs, so `speed` is the figure a card uses when a mark
// must hold its place in the water while the round goes on. The context
// carries the round's MEASURED speed, and the row's figure is the fallback
// for an object that hands none in.
//
// **A recipe is a pure function of the clock** (R915). It reads `c.t` and
// nothing else that moves, it keeps no state between frames, and it
// scatters with `grain(id, k)`. It allocates nothing (R131), and it clamps
// every alpha through `fill()` or `stroke()`.
//
// **A proportion is not a figure.** How far a breath swells a radius, how
// wide an arc opens, how small the last grain of a tail runs: these belong
// to the picture and stay here as named constants, the way `RAY_INNER` and
// `FOG_FLOOR` do in the kit.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import {
  TAU, breathe, cellScale, core, cycle, effectCellKey, fill, glow, grain, stroke,
  type EffectCtx, type EffectRecipeFn, type EffectRow,
} from './effect-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions of the cards
// ---------------------------------------------------------------------------

/** How wide a wake wanders off the track, as a fraction of the run. */
const WAKE_SPREAD = 0.15;
/** The smallest grain of a comet tail, as a fraction of the widest. */
const COMET_GRAIN_MIN = 0.25;
/** Where an ember trail starts astern, as a fraction of the run. */
const EMBER_START = 0.15;
/** The smallest ember, as a fraction of the widest. */
const EMBER_MIN = 0.46;
/** The peak of one ember, under the row's fill. */
const EMBER_PEAK = 0.85;
/** How far the ray of the seeker eye reads, against its arc. */
const EYE_RAY = 0.44;
/** How wide the eye sweeps off the heading, in radians. */
const EYE_SWEEP = 0.7;
/** Half of the eye's arc, in radians. */
const EYE_HALF = 0.3;
/** The lit fraction of one step of the interceptor thread. */
const THREAD_FILL = 0.22;
/** The inner hex of the lattice, against the outer stroke. */
const LATTICE_INNER = 0.56;
/** The six corners of a hexagon, and the corner that points to starboard. */
const HEX_SIDES = 6;
const HEX_TILT = -Math.PI / 6;
/** Where a spiral starts astern, as a fraction of the run. */
const SPIRAL_START = 0.075;
/** The smallest spiral grain, as a fraction of the widest. */
const SPIRAL_GRAIN_MIN = 0.4;
/** How far the twist closes over the run. */
const SPIRAL_TAPER = 0.35;
/** Half of the near bow arc, in radians, and how much the far arc closes. */
const BOW_HALF = 0.62, BOW_HALF_STEP = 0.12;
/** How far a bow arc swells with the breath, and its resting alpha. */
const BOW_SWELL = 0.12, BOW_REST = 0.35;
/** How far the pulse core swells with the breath, and its resting alpha. */
const CORE_SWELL = 0.38, CORE_REST = 0.45;
/** Where a twin tail leaves the fin, as a fraction of the V it opens to. */
const TWIN_ROOT = 0.25;
/** One twin-tail grain, as a fraction of the run, and how far it thins. */
const TWIN_GRAIN = 0.0375, TWIN_THIN = 0.5;
/** The smallest cavitation blob, as a fraction of the widest. */
const CAVITY_MIN = 0.42;
/** How wide a cavitation blob wanders, as a fraction of the run astern. */
const CAVITY_SPREAD = 0.13;
/** The smallest bubble of the ring, as a fraction of the widest. */
const BUBBLE_MIN = 0.5;
/** The nearest bubble stands here, and the set spreads out by this much. */
const BUBBLE_RING_MIN = 0.85, BUBBLE_RING_SPAN = 0.3;
/** How far the ring is squashed across the track. */
const BUBBLE_SQUASH = 0.8;
/** The resting brightness of one bubble, under its own peak. */
const BUBBLE_REST = 0.45;
/**
 * How many additive bands make the falloff of the searchlight cone.
 *
 * The band batch draws a hard edge, so one band would read as a cut-out
 * wedge. Five bands share the row's fill and each one reaches further, so
 * the light steps down toward the edge of the reach.
 */
const CONE_BANDS = 5;
/** How far the cone sways off the heading, and half of its opening. */
const CONE_SWAY = 0.42, CONE_HALF = 0.26;
/** The hard core inside a glow, as a fraction of the glow. */
const HARD_CORE = 0.16;
/** How far a lamp swells with the breath, and its resting alpha. */
const LAMP_SWELL = 0.55, LAMP_REST = 0.4;
/** The ping ring at birth, as a fraction of the ring at its widest. */
const PING_BIRTH = 1 / 6;
/** The smallest blob of a heavy wake, as a fraction of the widest. */
const HEAVY_MIN = 0.4;
/** How far a heavy blob swells with the breath, and its resting alpha. */
const HEAVY_SWELL = 0.12, HEAVY_REST = 0.7;
/** How far past the last blob the heavy wake fades out. */
const HEAVY_TAIL = 0.4;
/** How much smaller each chase pip runs, and how far the set dims. */
const PIP_SHRINK = 0.179, PIP_DIM = 0.8;
/** How far the chase flare swells with the breath, and its resting alpha. */
const CHASE_SWELL = 0.2, CHASE_REST = 0.6;
/** How far an anchor ring swells with the breath, and its resting alpha. */
const ANCHOR_SWELL = 0.1, ANCHOR_REST = 0.35;
/** halo_round, card 16: the disc's radius at the bottom of the breath. */
const HALO_REST = 0.85;
/** halo_round: how far the breath opens the disc's radius. */
const HALO_SWELL = 0.3;
/** halo_round: the disc's alpha at the bottom of the breath. */
const HALO_FLOOR = 0.5;

/**
 * How fast the round runs, in world px a second.
 *
 * The layer measures it off the drawn place (R924). A test, a still or an
 * object that hands none in reads 0, and the row's own `speed` figure — the
 * stage speed of the mockup — is then the fallback.
 */
function runPxSec(row: EffectRow, c: EffectCtx): number {
  const measured = c.speed ?? 0;
  return measured > 0 ? measured : row.speed;
}

/**
 * How long a mark left in the water lives: the reach `lengthPx` over the
 * round's run, so the chain is the same length at any speed, and the
 * period when no speed is at hand.
 */
function chainLife(row: EffectRow, c: EffectCtx): number {
  const spd = runPxSec(row, c);
  return spd > 0 && row.lengthPx > 0 ? row.lengthPx / spd : row.periodSec;
}

/** One value of a ladder of `n` steps, from `a` to `b`. */
function step(a: number, b: number, k: number, n: number): number {
  return n > 1 ? a + (b - a) * (k / (n - 1)) : a;
}

/**
 * The round recipes, by catalog code.
 *
 * Every function is one card of mockup round 042. The comment names the
 * card and states what the recipe writes, so the cap a test asks for is
 * read off the code.
 */
export const RECIPES: Readonly<Record<string, EffectRecipeFn>> = {
  /**
   * 042 card 01. A long tail of grains that thins to nothing astern.
   *
   * Writes `count` quads.
   */
  comet_tail(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const spread = WAKE_SPREAD * row.radiusPx;
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, grain(c.id, k + 1));
      const d = row.radius2Px + u * row.radiusPx;
      const w = (grain(c.id, k + 41) - 0.5) * spread * u;
      const r = row.widthPx * (COMET_GRAIN_MIN + (1 - COMET_GRAIN_MIN) * (1 - u));
      glow(c, e, c.x - cos * d - sin * w, c.y - sin * d + cos * w, cellScale(e, r),
        row.alpha * (1 - u));
    }
  },

  /**
   * 042 card 03. A ring leaves the track every period, spreads and fades.
   *
   * Writes `count` rings.
   */
  ring_wake(row, c) {
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
      const back = u * row.lengthPx;
      c.rings.ring(
        c.x - cos * back, c.y - sin * back,
        row.radius2Px + u * row.radiusPx, row.widthPx, c.color,
        stroke(row.alpha2 * (1 - u)) * c.alpha,
      );
    }
  },

  /**
   * 042 card 04. A faint arc sweeps ahead of the Seeker, with one ray on
   * the line it looks along. The row draws no fill.
   *
   * Writes 1 ring and 1 segment.
   */
  seeker_eye(row, c) {
    const look = c.heading + Math.sin(TAU * (c.t / row.periodSec + c.phase)) * EYE_SWEEP;
    c.rings.arc(
      c.x, c.y, row.radiusPx, row.widthPx,
      look - EYE_HALF, 2 * EYE_HALF, c.color, stroke(row.alpha2) * c.alpha,
    );
    c.segs.line(
      c.x, c.y,
      c.x + Math.cos(look) * row.radiusPx, c.y + Math.sin(look) * row.radiusPx,
      row.widthPx, c.color, stroke(row.alpha2 * EYE_RAY) * c.alpha,
    );
  },

  /**
   * 042 card 06. A fine dashed thread runs astern, and the dashes walk
   * astern at the speed the row states.
   *
   * Writes `count` segments.
   */
  interceptor_thread(row, c) {
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const gap = row.radiusPx / row.count;
    const on = gap * THREAD_FILL;
    // The walk wraps over the whole thread, so a dash that reached the tail
    // is the dash that leaves the nose. The far end is clipped at the
    // thread, which is what a dash offset on one line does.
    const walk = (row.speed * c.t) % row.radiusPx;
    const a = stroke(row.alpha) * c.alpha;
    for (let k = 0; k < row.count; k++) {
      const d0 = (walk + k * gap) % row.radiusPx;
      const d1 = Math.min(d0 + on, row.radiusPx);
      c.segs.line(
        c.x - cos * d0, c.y - sin * d0,
        c.x - cos * d1, c.y - sin * d1,
        row.widthPx, c.color, a,
      );
    }
  },

  /**
   * 042 card 07. A hex lattice turns round the Bulwark.
   *
   * Writes `6 * count` segments.
   */
  bulwark_lattice(row, c) {
    const turn = TAU * (row.speed * c.t + c.phase);
    for (let k = 0; k < row.count; k++) {
      const r = step(row.radiusPx, row.radius2Px, k, row.count);
      const a = stroke(row.alpha2 * step(1, LATTICE_INNER, k, row.count)) * c.alpha;
      let px = c.x + Math.cos(turn + HEX_TILT) * r;
      let py = c.y + Math.sin(turn + HEX_TILT) * r;
      for (let s = 1; s <= HEX_SIDES; s++) {
        const ang = turn + HEX_TILT + (s / HEX_SIDES) * TAU;
        const qx = c.x + Math.cos(ang) * r, qy = c.y + Math.sin(ang) * r;
        c.segs.line(px, py, qx, qy, row.widthPx, c.color, a);
        px = qx; py = qy;
      }
    }
  },

  /**
   * 042 card 09. The round sheds dull glow grains astern, and every grain
   * swells and goes out over one period.
   *
   * Writes `count` quads.
   */
  ember_trail(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const spread = WAKE_SPREAD * row.radiusPx;
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, grain(c.id, k + 1));
      const d = EMBER_START * row.radiusPx + u * row.radiusPx;
      const w = (grain(c.id, k + 31) - 0.5) * spread * u;
      const r = row.radius2Px * (EMBER_MIN + (1 - EMBER_MIN) * u);
      glow(c, e, c.x - cos * d - sin * w, c.y - sin * d + cos * w, cellScale(e, r),
        row.alpha * Math.sin(Math.PI * u) * EMBER_PEAK);
    }
  },

  /**
   * 042 card 10. Grains twist round the track and thin away astern.
   *
   * Writes `count` quads.
   */
  spiral(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    // The twist the row asks for over one run: revolutions a second, over
    // the seconds one grain lives.
    const turns = row.speed * row.periodSec;
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, k / row.count);
      const d = SPIRAL_START * row.lengthPx + u * row.lengthPx;
      const th = u * TAU * turns + c.phase * TAU;
      const off = Math.sin(th) * row.radiusPx * (1 - SPIRAL_TAPER * u);
      const near = 0.5 + 0.5 * Math.cos(th);
      const r = row.widthPx * (SPIRAL_GRAIN_MIN + (1 - SPIRAL_GRAIN_MIN) * near);
      glow(c, e, c.x - cos * d - sin * off, c.y - sin * d + cos * off, cellScale(e, r),
        row.alpha * (1 - u) * (0.5 + 0.5 * near));
    }
  },

  /**
   * 042 card 11. Small arcs stand ahead of the nose and pulse.
   *
   * Writes `count` rings.
   */
  bow_arcs(row, c) {
    for (let k = 0; k < row.count; k++) {
      const b = breathe(c.t, row.periodSec, c.phase + k / (2 * row.count));
      const r = step(row.radiusPx, row.radius2Px, k, row.count)
        * (1 - BOW_SWELL / 2 + BOW_SWELL * b);
      const half = BOW_HALF - BOW_HALF_STEP * k;
      c.rings.arc(
        c.x, c.y, r, row.widthPx, c.heading - half, 2 * half, c.color,
        stroke(row.alpha * (BOW_REST + (1 - BOW_REST) * b)) * c.alpha,
      );
    }
  },

  /**
   * 042 card 12. The glow of the round breathes and never goes out, over
   * one hard core.
   *
   * Writes 1 quad and 1 ring.
   */
  pulse_core(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const b = breathe(c.t, row.periodSec, c.phase);
    const r = row.radiusPx * (1 - CORE_SWELL / 2 + CORE_SWELL * b);
    glow(c, e, c.x, c.y, cellScale(e, r),
      row.alpha * (CORE_REST + (1 - CORE_REST) * b));
    core(c, c.x, c.y, row.radius2Px, row.alpha2);
  },

  /**
   * 042 card 13. Two short tails of grains open astern in a V, one off
   * each fin.
   *
   * Writes `count * count2` quads.
   */
  twin_tails(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const tails = Math.max(1, row.count2);
    for (let j = 0; j < tails; j++) {
      const side = tails > 1 ? -1 + (2 * j) / (tails - 1) : 0;
      for (let k = 0; k < row.count; k++) {
        const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
        const d = row.radius2Px + u * row.radiusPx;
        const off = side * row.widthPx * (TWIN_ROOT + u);
        const r = TWIN_GRAIN * row.radiusPx * (1 - TWIN_THIN * u);
        glow(c, e, c.x - cos * d - sin * off, c.y - sin * d + cos * off,
          cellScale(e, r), row.alpha * Math.sin(Math.PI * u));
      }
    }
  },

  /**
   * 042 card 14. Fog blobs leave the round astern, drift wide and thin
   * away.
   *
   * Writes `count` quads.
   */
  cavitation_cloud(row, c) {
    const e = c.sprite(effectCellKey('fog'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const spread = CAVITY_SPREAD * row.lengthPx;
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
      const d = row.radius2Px + u * row.lengthPx;
      const w = (grain(c.id, k + 1) - 0.5) * 2 * spread * u;
      const r = row.radiusPx * (CAVITY_MIN + (1 - CAVITY_MIN) * u);
      // The card draws an ellipse that lies along the track. The baked fog
      // cell is round, so the blob keeps the size and loses the stretch.
      glow(c, e, c.x - cos * d - sin * w, c.y - sin * d + cos * w, cellScale(e, r),
        row.alpha * Math.sin(Math.PI * u));
    }
  },

  /**
   * 042 card 19. The round drops a small ring every `lengthPx / count`
   * world px of its run. Each ring holds its place in the water and fades
   * before the chain reaches `lengthPx` astern, so the chain reads the
   * same at the mockup's stage speed and at a live round's run: a ring
   * lives `lengthPx / speed` seconds, and with no speed at hand it drifts
   * astern over the period instead.
   *
   * Writes `count` rings.
   */
  ring_chain(row, c) {
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const life = chainLife(row, c);
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, life, c.phase + k / row.count);
      const back = u * row.lengthPx;
      c.rings.ring(
        c.x - cos * back, c.y - sin * back,
        row.radiusPx + u * row.radius2Px, row.widthPx, c.color,
        stroke(row.alpha * (1 - u)) * c.alpha,
      );
    }
  },

  /**
   * 042 card 21. A ring of bubbles stands round the round and turns.
   *
   * Writes `count` quads.
   */
  bubble_ring(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const turn = TAU * (row.speed * c.t + c.phase);
    for (let k = 0; k < row.count; k++) {
      const h = grain(c.id, k + 1);
      const th = turn + (k / row.count) * TAU;
      const r = row.radiusPx * (BUBBLE_RING_MIN + BUBBLE_RING_SPAN * h);
      const f = BUBBLE_REST + (1 - BUBBLE_REST)
        * Math.sin(Math.PI * cycle(c.t, row.periodSec, h));
      glow(c, e, c.x + Math.cos(th) * r, c.y + Math.sin(th) * r * BUBBLE_SQUASH,
        cellScale(e, row.widthPx * (BUBBLE_MIN + (1 - BUBBLE_MIN) * h)), row.alpha * f);
    }
  },

  /**
   * 042 card 22. A faint cone stands ahead of the nose and sways across
   * the track.
   *
   * Writes `CONE_BANDS` rings and 2 segments.
   */
  searchlight(row, c) {
    const th = c.heading + Math.sin(TAU * (c.t / row.periodSec + c.phase)) * CONE_SWAY;
    const band = fill(row.alpha / CONE_BANDS) * c.alpha;
    // Every band starts at the round and one reaches further than the last,
    // so the light they add together steps down toward the reach.
    for (let k = 0; k < CONE_BANDS; k++) {
      const reach = (row.radiusPx * (k + 1)) / CONE_BANDS;
      c.rings.arc(
        c.x, c.y, reach / 2, reach, th - CONE_HALF, 2 * CONE_HALF,
        c.color, band, c.add,
      );
    }
    const a = stroke(row.alpha2) * c.alpha;
    for (let s = -1; s <= 1; s += 2) {
      const edge = th + s * CONE_HALF;
      c.segs.line(
        c.x, c.y,
        c.x + Math.cos(edge) * row.radiusPx, c.y + Math.sin(edge) * row.radiusPx,
        row.widthPx, c.color, a,
      );
    }
  },

  /**
   * 042 card 23. A lamp sits on the nose of the Light and breathes.
   *
   * Writes 1 quad and 1 ring.
   */
  light_lantern(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const b = breathe(c.t, row.periodSec, c.phase);
    const nx = c.x + Math.cos(c.heading) * row.radius2Px;
    const ny = c.y + Math.sin(c.heading) * row.radius2Px;
    const r = row.radiusPx * (1 - LAMP_SWELL / 2 + LAMP_SWELL * b);
    glow(c, e, nx, ny, cellScale(e, r), row.alpha * (LAMP_REST + (1 - LAMP_REST) * b));
    core(c, nx, ny, HARD_CORE * row.radiusPx, row.alpha2);
  },

  /**
   * 042 card 24. A ping ring leaves the nose of the Seeker and holds its
   * place in the water while the round runs on. A ring falls at most
   * `lengthPx` astern of the nose before it fades, at any speed: it lives
   * `lengthPx / speed` seconds, and with no speed at hand it drifts over
   * the period instead.
   *
   * Writes `count` rings.
   */
  seeker_pulse(row, c) {
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const life = chainLife(row, c);
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, life, c.phase + k / row.count);
      const ahead = row.radius2Px - u * row.lengthPx;
      c.rings.ring(
        c.x + cos * ahead, c.y + sin * ahead,
        row.radiusPx * (PING_BIRTH + u), row.widthPx, c.color,
        stroke(row.alpha * (1 - u)) * c.alpha,
      );
    }
  },

  /**
   * 042 card 27. A wide fog wake lies astern of the Heavy and widens
   * behind it.
   *
   * Writes `count` quads.
   */
  heavy_wake(row, c) {
    const e = c.sprite(effectCellKey('fog'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const b = breathe(c.t, row.periodSec, c.phase);
    for (let k = 0; k < row.count; k++) {
      const d = row.radius2Px + k * row.lengthPx;
      const r = row.radiusPx * step(HEAVY_MIN, 1, k, row.count)
        * (1 - HEAVY_SWELL / 2 + HEAVY_SWELL * b);
      const f = 1 - k / (row.count + HEAVY_TAIL);
      // The card's blob is an ellipse; the baked fog cell is round.
      glow(c, e, c.x - cos * d, c.y - sin * d, cellScale(e, r),
        row.alpha * f * (HEAVY_REST + (1 - HEAVY_REST) * b));
    }
  },

  /**
   * 042 card 28. A bright core runs on the Interceptor with a short tail
   * of pips.
   *
   * Writes 1 quad and `count2 + 1` rings.
   */
  chase_flare(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const b = breathe(c.t, row.periodSec, c.phase);
    for (let k = 1; k <= row.count2; k++) {
      const d = k * row.lengthPx;
      core(c, c.x - cos * d, c.y - sin * d, row.radius2Px * (1 - PIP_SHRINK * k),
        row.alpha * (1 - k / (row.count2 + 1)) * PIP_DIM);
    }
    const r = row.radiusPx * (1 - CHASE_SWELL / 2 + CHASE_SWELL * b);
    glow(c, e, c.x, c.y, cellScale(e, r),
      row.alpha * (CHASE_REST + (1 - CHASE_REST) * b));
    core(c, c.x, c.y, HARD_CORE * row.radiusPx, row.alpha2);
  },

  /**
   * 042 card 30. Rings stand round the Bulwark and take the breath in
   * turn.
   *
   * Writes `count` rings.
   */
  anchor_rings(row, c) {
    for (let k = 0; k < row.count; k++) {
      const b = breathe(c.t, row.periodSec, c.phase + k / row.count);
      const r = step(row.radiusPx, row.radius2Px, k, row.count)
        * (1 - ANCHOR_SWELL / 2 + ANCHOR_SWELL * b);
      c.rings.ring(
        c.x, c.y, r, row.widthPx, c.color,
        stroke(row.alpha * (ANCHOR_REST + (1 - ANCHOR_REST) * b)) * c.alpha,
      );
    }
  },

  /**
   * 042 card 16. One soft disc lies under the round and breathes (R960).
   * The radius and the alpha open together, and the alpha rests on a floor,
   * so the mark never goes out.
   *
   * Writes 1 quad.
   */
  halo_round(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const b = breathe(c.t, row.periodSec, c.phase);
    glow(c, e, c.x, c.y, cellScale(e, row.radiusPx * (HALO_REST + HALO_SWELL * b)),
      row.alpha * (HALO_FLOOR + (1 - HALO_FLOOR) * b));
  },
};
